/* Supplier-customer invites — backed by T-311.
 *
 * Suppliers send a customer a dedicated URL pre-pinning a phone number
 * and a target (store visit, product, or subscription bundle). The
 * customer clicks the URL → OTP (skipped if phone matches an existing
 * session) → lands in the supplier's locked surface until payment.
 *
 * Mock fallback persists invites in localStorage so the dev-mode flow
 * (creating in VendorDashboard, opening in a new tab) works end-to-end.
 */

import { api, mockOr } from '../lib/api.js';
import { D } from '../mock-data.js';

const STORE_KEY = 'rekraft.supplierInvites';
const EXPIRY_DAYS = 7;

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (obj) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(obj)); } catch {}
};

const randomToken = () => {
  const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < 10; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
};

const buildUrl = (token) => {
  const base = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';
  return `${base}?invite=${token}`;
};

const branchNameFor = (branchId) => D.branches.grocery.find((b) => b.id === branchId)?.name || '';

export const seedMockInvites = () => {
  const existing = readStore();
  if (Object.keys(existing).length > 0) return;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  const seed = {
    invabc12345: { token: 'invabc12345', branchId: 'g1', customerPhone: '9876512345', customerName: 'Priya S.', target: { kind: 'product', productId: 'p101', variantLabel: '500 ml' }, status: 'paid',            createdAt: new Date(now - 3 * day).toISOString(), expiresAt: new Date(now + 4 * day).toISOString(), completedAt: new Date(now - 2 * day).toISOString(), orderId: 'ORD-9201', revenueP: 6200 },
    invdef67890: { token: 'invdef67890', branchId: 'g1', customerPhone: '9876512346', customerName: 'Rahul M.',  target: { kind: 'bundle', items: [{ productId: 'p101', variantLabel: '1 L', qty: 1, cadence: 'Daily' }, { productId: 'p103', variantLabel: '500 g', qty: 1, cadence: 'Weekly' }], deliveryWindow: '6–8 AM' }, status: 'pending_payment', createdAt: new Date(now - 1 * day).toISOString(), expiresAt: new Date(now + 6 * day).toISOString() },
    invghi24680: { token: 'invghi24680', branchId: 'g1', customerPhone: '9876512347', customerName: 'Anitha K.', target: { kind: 'store' }, status: 'pending_otp',     createdAt: new Date(now - 2 * 60 * 60 * 1000).toISOString(), expiresAt: new Date(now + 7 * day).toISOString() },
    invjkl13579: { token: 'invjkl13579', branchId: 'g1', customerPhone: '9876512348', customerName: 'Suresh P.', target: { kind: 'product', productId: 'p103', variantLabel: '500 g' }, status: 'expired',          createdAt: new Date(now - 14 * day).toISOString(), expiresAt: new Date(now - 7 * day).toISOString() },
  };
  writeStore(seed);
};

export async function createSupplierInvite({ branchId, customerPhone, customerName, target }) {
  return mockOr(
    () => api.post('/supplier-invites', { branchId, customerPhone, customerName, target }, { idempotencyScope: 'supplier-invites.create' }),
    () => {
      const token = randomToken();
      const now = Date.now();
      const invite = {
        token,
        branchId,
        customerPhone,
        customerName: customerName || null,
        target,
        status: 'pending_otp',
        createdAt: new Date(now).toISOString(),
        expiresAt: new Date(now + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      };
      const store = readStore();
      store[token] = invite;
      writeStore(store);
      const url = buildUrl(token);
      if (typeof console !== 'undefined') console.log('[supplierInvites] Created invite:', url);
      return { token, url, expiresAt: invite.expiresAt };
    },
  );
}

export async function resolveSupplierInvite(token) {
  return mockOr(
    () => api.get(`/supplier-invites/${token}/resolve`),
    () => {
      const store = readStore();
      const inv = store[token];
      if (!inv) return null;
      const expired = new Date(inv.expiresAt).getTime() < Date.now();
      if (expired && inv.status !== 'paid' && inv.status !== 'cancelled') {
        inv.status = 'expired';
        writeStore(store);
      }
      return {
        token,
        phone: inv.customerPhone,
        customerName: inv.customerName,
        branchId: inv.branchId,
        branchName: branchNameFor(inv.branchId),
        target: inv.target,
        status: inv.status,
        expiresAt: inv.expiresAt,
      };
    },
  );
}

export async function listSupplierInvites({ branchId } = {}) {
  return mockOr(
    () => api.get('/supplier-invites', { query: { branchId } }),
    () => {
      const store = readStore();
      const now = Date.now();
      const out = [];
      let dirty = false;
      for (const t of Object.keys(store)) {
        const inv = store[t];
        if (branchId && inv.branchId !== branchId) continue;
        const expired = new Date(inv.expiresAt).getTime() < now;
        if (expired && inv.status !== 'paid' && inv.status !== 'cancelled' && inv.status !== 'expired') {
          inv.status = 'expired';
          dirty = true;
        }
        out.push({ ...inv, branchName: branchNameFor(inv.branchId) });
      }
      if (dirty) writeStore(store);
      out.sort((a, b) => (new Date(b.createdAt)) - (new Date(a.createdAt)));
      return out;
    },
  );
}

export async function completeSupplierInvite(token, { orderId, revenueP } = {}) {
  return mockOr(
    () => api.post(`/supplier-invites/${token}/complete`, { orderId, revenueP }, { idempotencyScope: 'supplier-invites.complete:' + token }),
    () => {
      const store = readStore();
      const inv = store[token];
      if (!inv) return { ok: false };
      inv.status = 'paid';
      inv.completedAt = new Date().toISOString();
      if (orderId) inv.orderId = orderId;
      if (revenueP) inv.revenueP = revenueP;
      writeStore(store);
      return { ok: true, status: 'paid' };
    },
  );
}

export async function cancelSupplierInvite(token) {
  return mockOr(
    () => api.post(`/supplier-invites/${token}/cancel`, {}, { idempotencyScope: 'supplier-invites.cancel:' + token }),
    () => {
      const store = readStore();
      const inv = store[token];
      if (!inv) return { ok: false };
      inv.status = 'cancelled';
      writeStore(store);
      return { ok: true, status: 'cancelled' };
    },
  );
}
